import { SFXEngine } from './SFXEngine';
import { MusicEngine } from './MusicEngine';
import type { GameStore } from '../store/GameStore';
import type { SimulationState } from '../core/types';

/**
 * Central audio controller.
 * Manages SFX + music, subscribes to game events, handles mute/volume.
 * Dynamically switches music mood based on game state.
 */
export class AudioManager {
  private sfx: SFXEngine;
  private music: MusicEngine;
  private unsubscribers: (() => void)[] = [];
  private started: boolean = false;
  private muted: boolean = false;

  constructor(store: GameStore) {
    this.sfx = new SFXEngine();
    this.music = new MusicEngine();

    // Subscribe to game events for SFX
    this.unsubscribers.push(
      store.on('STAFF_LEVELED_UP', () => this.sfx.levelUp()),
      store.on('PROJECT_COMPLETED', () => this.sfx.success()),
      store.on('REVIEW_SCORES_GENERATED', (e) => {
        if (e.type === 'REVIEW_SCORES_GENERATED') {
          if (e.isHallOfFame) this.sfx.levelUp();
          else if (e.average >= 6) this.sfx.reviewHigh();
          else this.sfx.reviewLow();
        }
      }),
      store.on('STAFF_DEMAND', () => this.sfx.warning()),
      store.on('STAFF_LEFT', () => this.sfx.error()),
      store.on('LOW_FUNDS_WARNING', () => this.sfx.warning()),
      store.on('UNLOCK_ACHIEVED', () => this.sfx.unlock()),
      store.on('COMPANY_LEVELED_UP', () => this.sfx.levelUp()),
      store.on('CONTRACT_COMPLETED', () => this.sfx.coin()),
      store.on('GAME_OVER', () => {
        this.sfx.gameOver();
        this.music.stop();
      }),
      store.on('STAFF_HIRED', () => this.sfx.success()),
      store.on('OFFICE_UPGRADED', () => this.sfx.unlock()),
      store.on('CANDIDATES_READY', () => this.sfx.success()),
      store.on('RANDOM_EVENT', () => this.sfx.warning()),
      store.on('SCRIPTED_EVENT', () => this.sfx.success()),
      store.on('PROJECT_READY_TO_SHIP', () => this.sfx.success()),
      store.on('ACHIEVEMENT_UNLOCKED', () => this.sfx.unlock()),
    );
  }

  async start(): Promise<void> {
    if (this.started) return;
    await this.sfx.init();
    await this.music.init();
    this.started = true;
    if (!this.muted) {
      this.music.play('calm');
    }
  }

  /** Update music mood based on current game state. Call periodically. */
  updateMood(state: SimulationState): void {
    if (!this.started || this.muted) return;
    this.music.ensurePlaying();

    // Tense: low funds or in debt
    const weeklyBurn = state.staff.reduce((s, st) => s + st.salary, 0) + state.office.weeklyRent;
    const weeksOfRunway = weeklyBurn > 0 ? state.company.funds / weeklyBurn : 999;

    if (state.company.weeksInDebt > 0 || weeksOfRunway < 4) {
      this.music.setMood('tense');
      return;
    }

    // Triumph: after a Hall of Fame game (lasts a while via natural mood switching)
    if (state.company.hallOfFameCount > 0 && state.company.level >= 5 && state.company.fans > 5000) {
      this.music.setMood('triumph');
      return;
    }

    // Upbeat: working on projects with a decent team
    const hasActiveProject = state.projects.some((p) => p.phase !== 'complete');
    if (hasActiveProject && state.staff.length >= 3) {
      this.music.setMood('upbeat');
      return;
    }

    // Chill: idle with money, no projects
    if (!hasActiveProject && state.company.funds > 10000) {
      this.music.setMood('chill');
      return;
    }

    // Default: calm
    this.music.setMood('calm');
  }

  click(): void { this.sfx.click(); }

  setMusicVolume(vol: number): void { this.music.setVolume(vol); }
  setSFXVolume(vol: number): void { this.sfx.setVolume(vol); }

  toggleMute(): void {
    this.muted = !this.muted;
    if (this.muted) {
      this.music.stop();
    } else if (this.started) {
      this.music.play('calm');
    }
  }

  get isMuted(): boolean { return this.muted; }

  destroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.sfx.destroy();
    this.music.destroy();
  }
}
